/**
 * Helpers for serving Norce CDN images through a plain `<img>`.
 *
 * The Norce media CDN resizes on `w=`, so a srcset can be built by hand. Doing
 * that instead of going through `<NuxtImg>` leaves exactly ONE mechanism
 * deciding what the browser downloads — the `srcset`/`sizes` pair the parser
 * evaluates once. NuxtImg adds a second one (`<link rel="preload"
 * imagesrcset>`) next to a provider-generated srcset, and the two can resolve
 * to different candidates, so the same photo gets fetched twice.
 *
 * This mirrors what breadandboxers has been running in production; see the
 * AGENTS.md note on the `<NuxtImg>` rule.
 */

/** Widths offered to the browser for a full-width/near-full-width image. */
const BIG_IMAGE_WIDTHS = [640, 960, 1200, 1600, 1920, 2560, 3200];

export const appendParams = (src: string, params: string) => {
	const separator = src.includes("?") ? "&" : "?";
	return `${src}${separator}${params}`;
};

export const isNorceCdnUrl = (src?: string | null) =>
	!!src && src.includes("cdn-norce.tech");

export const buildNorceSrcSet = (src: string, widths = BIG_IMAGE_WIDTHS) => {
	if (!isNorceCdnUrl(src)) return "";

	return widths.map((w) => `${appendParams(src, `w=${w}`)} ${w}w`).join(", ");
};

/** Widths for a product-card image: half the viewport on phones, a quarter above. */
export const CARD_IMAGE_WIDTHS = [160, 256, 384, 512, 640, 768];

/** `sizes` matching the 2-column (mobile) / 4-column grid the cards sit in. */
export const CARD_IMAGE_SIZES = "(max-width: 767px) 50vw, 25vw";

/**
 * `src` and `srcset` for a product-card image on the Norce CDN, built by hand
 * like the gallery's (see above) so a preloaded first row cannot download
 * the same photo twice.
 */
export const buildNorceCardImage = (path: string) => {
	const base = appendParams(path, "mode=max&format=webp");
	return {
		src: appendParams(base, "w=384"),
		srcset: buildNorceSrcSet(base, CARD_IMAGE_WIDTHS)
	};
};
